import { useState, useEffect, FormEvent } from 'react'
import { apiClient } from '@/services/api/client'
import { ultraadminApi, type UaSchool, CountryInfo, RegionInfo } from '@/services/api/ultraadmin'
import { ultraadminPartnersApi, type PartnerCompanyAdmin } from '@/services/api/partner'
import { extractErrorMessage } from '@/utils/errorHandler'
import { SchoolListFilters } from '@/components/ultraadmin/SchoolListFilters'
import {
  EMPTY_SCHOOL_LIST_FILTER,
  SCHOOL_LANGUAGES,
  schoolListFilterParams,
  type SchoolListFilterValue,
} from '@/components/ultraadmin/schoolListFilter'

interface SchoolFormData {
  name: string
  short_name: string
  address: string
  city: string
  zip_code: string
  country: string
  region_id: string
  email: string
  website: string
  phone: string
  logo_url: string
  language: string
  partner_company_id: string
  billing_currency: string
  timezone: string
}

const LANGUAGE_OPTIONS = SCHOOL_LANGUAGES

const emptyForm: SchoolFormData = {
  name: '', short_name: '', address: '', city: '',
  zip_code: '', country: 'CH', region_id: '', email: '', website: '',
  phone: '', logo_url: '', language: 'de', partner_company_id: '', billing_currency: 'CHF', timezone: 'Europe/Zurich',
}

export function SchoolManagementPage() {
  const [schools, setSchools] = useState<UaSchool[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [editing, setEditing] = useState<UaSchool | null>(null)
  const [showDomain, setShowDomain] = useState(false)
  const [selected, setSelected] = useState<UaSchool | null>(null)
  const [form, setForm] = useState<SchoolFormData>(emptyForm)
  const [domainForm, setDomainForm] = useState({ domain: '', is_primary: false })
  const [error, setError] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [countries, setCountries] = useState<CountryInfo[]>([])
  const [regions, setRegions] = useState<RegionInfo[]>([])
  const [loadingCountries, setLoadingCountries] = useState(false)
  const [filters, setFilters] = useState<SchoolListFilterValue>(EMPTY_SCHOOL_LIST_FILTER)
  const [debouncedFilters, setDebouncedFilters] = useState(filters)
  const [partners, setPartners] = useState<PartnerCompanyAdmin[]>([])
  const [assignSchool, setAssignSchool] = useState<UaSchool | null>(null)
  const [assignCompanyId, setAssignCompanyId] = useState('')

  const fetchSchools = async (opts?: { quiet?: boolean }) => {
    if (!opts?.quiet) setLoading(true)
    try {
      const rows = await ultraadminApi.listSchools(schoolListFilterParams(debouncedFilters))
      setSchools(rows)
      return rows
    } catch {
      setError('Schulen konnten nicht geladen werden')
      return [] as UaSchool[]
    } finally {
      if (!opts?.quiet) setLoading(false)
    }
  }

  const fetchCountries = async () => {
    setLoadingCountries(true)
    try {
      const data = await ultraadminApi.getCountries()
      setCountries(data)
    } catch {
      // Fallback to empty
    } finally {
      setLoadingCountries(false)
    }
  }

  const fetchRegions = async (countryCode: string) => {
    if (!countryCode) {
      setRegions([])
      return
    }
    try {
      const data = await ultraadminApi.getCountryRegions(countryCode)
      setRegions(data)
    } catch {
      setRegions([])
    }
  }

  const handleCountryChange = (countryCode: string) => {
    setForm({ ...form, country: countryCode, region_id: '' })
    fetchRegions(countryCode)
  }

  const closeForm = () => {
    setShowCreate(false)
    setEditing(null)
    setForm(emptyForm)
    setRegions([])
    setFormError(null)
  }

  const openCreate = () => {
    setEditing(null)
    setFormError(null)
    setForm(emptyForm)
    setShowCreate(true)
    fetchCountries()
    fetchRegions(emptyForm.country)
  }

  const openEdit = (s: UaSchool) => {
    setShowCreate(false)
    setFormError(null)
    setEditing(s)
    setForm({
      name: s.name,
      short_name: s.short_name || '',
      address: s.address || '',
      city: s.city || '',
      zip_code: s.zip_code || '',
      country: s.country || 'CH',
      region_id: s.region_id || '',
      email: s.email || '',
      website: s.website || '',
      phone: s.phone || '',
      logo_url: s.logo_url || '',
      language: s.language || 'de',
      partner_company_id: s.partner_company_id || '',
      billing_currency: s.billing_currency || 'CHF',
      timezone: s.timezone || 'Europe/Zurich',
    })
    fetchCountries()
    fetchRegions(s.country || 'CH')
  }

  const saveSchool = async (e: FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      const stammdaten = {
        name: form.name,
        short_name: form.short_name,
        address: form.address || null,
        city: form.city || null,
        zip_code: form.zip_code || null,
        country: form.country,
        region_id: form.region_id || null,
        language: form.language,
        email: form.email || null,
        website: form.website || null,
        phone: form.phone || null,
        logo_url: form.logo_url || null,
        timezone: form.timezone,
      }
      if (editing) {
        const nextPartner = form.partner_company_id || null
        const row = await ultraadminApi.updateSchool(editing.id, {
          name: stammdaten.name,
          short_name: stammdaten.short_name,
          address: stammdaten.address ?? undefined,
          city: stammdaten.city ?? undefined,
          zip_code: stammdaten.zip_code ?? undefined,
          country: stammdaten.country,
          region_id: stammdaten.region_id ?? undefined,
          language: stammdaten.language,
          email: stammdaten.email ?? undefined,
          website: stammdaten.website ?? undefined,
          phone: stammdaten.phone ?? undefined,
          logo_url: stammdaten.logo_url ?? undefined,
          timezone: stammdaten.timezone,
          ...((editing.partner_company_id || null) !== nextPartner ? { partner_company_id: nextPartner } : {}),
        })
        setSchools((prev) => prev.map((x) => (x.id === row.id ? { ...x, ...row } : x)))
      } else {
        await apiClient.post('/api/v1/ultraadmin/schools', {
          ...stammdaten,
          address: stammdaten.address || undefined,
          city: stammdaten.city || undefined,
          zip_code: stammdaten.zip_code || undefined,
          region_id: stammdaten.region_id || undefined,
          email: stammdaten.email || undefined,
          website: stammdaten.website || undefined,
          phone: stammdaten.phone || undefined,
          logo_url: stammdaten.logo_url || undefined,
          partner_company_id: form.partner_company_id || undefined,
          billing_currency: form.billing_currency,
        })
      }
      closeForm()
      setError(null)
      await fetchSchools()
    } catch (e: unknown) {
      const errorMessage = extractErrorMessage(e, { loadError: editing ? 'Fehler beim Speichern' : 'Fehler beim Erstellen' })
      setFormError(errorMessage)
    } finally {
      setSubmitting(false)
    }
  }

  const openDomainModal = (s: UaSchool) => {
    setSelected(s)
    setDomainForm({ domain: '', is_primary: false })
    setError(null)
    setShowDomain(true)
  }

  const addDomain = async (e: FormEvent) => {
    e.preventDefault()
    if (!selected) return
    setSubmitting(true)
    try {
      await apiClient.post(
        `/api/v1/ultraadmin/schools/${selected.id}/domains`,
        domainForm,
      )
      setShowDomain(false)
      await fetchSchools()
    } catch (e: unknown) {
      const errorMessage = extractErrorMessage(e, { loadError: 'Fehler beim Hinzufügen' })
      setError(errorMessage)
    } finally {
      setSubmitting(false)
    }
  }

  const deactivateSchool = async (s: UaSchool) => {
    if (!confirm(`Schule "${s.name}" wirklich deaktivieren?`)) return
    try {
      await apiClient.post(`/api/v1/ultraadmin/schools/${s.id}/deactivate`)
      await fetchSchools()
    } catch (e: unknown) {
      const errorMessage = extractErrorMessage(e, { loadError: 'Fehler beim Deaktivieren' })
      setError(errorMessage)
    }
  }

  const removeDomain = async (schoolId: string, domainId: string, d: string) => {
    if (!confirm(`Domain "${d}" wirklich entfernen?`)) return
    try {
      await apiClient.delete(`/api/v1/ultraadmin/schools/${schoolId}/domains/${domainId}`)
      await fetchSchools()
    } catch (e: unknown) {
      const errorMessage = extractErrorMessage(e, { loadError: 'Fehler beim Entfernen' })
      setError(errorMessage)
    }
  }

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedFilters(filters), 250)
    return () => window.clearTimeout(t)
  }, [filters])

  useEffect(() => {
    fetchSchools()
  }, [debouncedFilters]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchCountries()
    ultraadminPartnersApi.list().then(setPartners).catch(() => undefined)
  }, [])

  const getCountryName = (code: string) => {
    const c = countries.find(c => c.code === code)
    return c?.name || code
  }

  const getRegionName = (school: UaSchool) => {
    if (school.region?.name) return school.region.name
    if (school.region_name) return school.region_name
    return null
  }

  const getLanguageLabel = (code: string | null) => {
    if (!code) return '-'
    const lang = LANGUAGE_OPTIONS.find(l => l.code === code)
    return lang?.label || code
  }

  const partnerChoices = (currentId: string) =>
    partners.filter((p) => p.is_active || p.id === currentId)

  return (
    <div className="p-6 min-w-0">
      <div className="flex justify-between items-center gap-4 mb-6">
        <h1 className="text-2xl font-bold whitespace-nowrap">Schulverwaltung</h1>
        <button onClick={openCreate} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 shrink-0">
          + Neue Schule
        </button>
      </div>
      <SchoolListFilters value={filters} onChange={setFilters} countries={countries} resultCount={schools.length} />
      {loading && <div className="mb-4 text-sm text-gray-500">Laden...</div>}

      {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{error}</div>}

      <div className="bg-white rounded-lg shadow overflow-x-auto max-w-full">
        <table className="min-w-full divide-y divide-gray-200 whitespace-nowrap text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase sticky left-0 z-20 bg-gray-50">Name</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kurzname</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Land</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Region</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sprache</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">E-Mail</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Domains</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partner</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase sticky right-0 z-20 bg-gray-50 shadow-[-6px_0_8px_-4px_rgba(0,0,0,0.12)]">Aktionen</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {schools.map((s) => (
              <tr key={s.id} className="group">
                <td className="px-3 py-3 sticky left-0 z-10 bg-white group-hover:bg-gray-50">{s.name}</td>
                <td className="px-3 py-3">{s.short_name || '-'}</td>
                <td className="px-3 py-3">{getCountryName(s.country)}</td>
                <td className="px-3 py-3">{getRegionName(s) || '-'}</td>
                <td className="px-3 py-3">{getLanguageLabel(s.language)}</td>
                <td className="px-3 py-3">{s.email || '-'}</td>
                <td className="px-3 py-3">
                  <div className="flex gap-1">
                    {s.domains.map((d) => (
                      <span key={d.id} className={`px-2 py-1 text-xs rounded ${d.is_primary ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}`}>
                        {d.domain}
                        <button onClick={() => removeDomain(s.id, d.id, d.domain)} className="ml-1 text-red-500 hover:text-red-700">×</button>
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-3 py-3">{s.partner_company_name || 'Direkt'}</td>
                <td className="px-3 py-3">
                  <span className={`px-2 py-1 text-xs rounded-full ${s.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {s.is_active ? 'Aktiv' : 'Inaktiv'}
                  </span>
                </td>
                <td className="px-3 py-3 text-sm sticky right-0 z-10 bg-white group-hover:bg-gray-50 shadow-[-6px_0_8px_-4px_rgba(0,0,0,0.12)] whitespace-normal">
                  <div className="flex flex-col items-start gap-1 min-w-[9.5rem]">
                    {s.short_name !== 'SYSTEM' && (
                      <button type="button" onClick={() => openEdit(s)} className="text-blue-600 hover:text-blue-900 whitespace-nowrap">Bearbeiten</button>
                    )}
                    <button type="button" onClick={() => openDomainModal(s)} className="text-blue-600 hover:text-blue-900 whitespace-nowrap">Domain hinzufügen</button>
                    {s.short_name !== 'SYSTEM' && (
                      <button type="button" onClick={() => { setAssignSchool(s); setAssignCompanyId(s.partner_company_id || ''); setError(null) }} className="text-teal-700 hover:text-teal-900 whitespace-nowrap">Zuordnen</button>
                    )}
                    {s.is_active && (
                      <button type="button" onClick={() => deactivateSchool(s)} className="text-red-600 hover:text-red-900 whitespace-nowrap">Deaktivieren</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(showCreate || editing) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">{editing ? 'Schule bearbeiten' : 'Neue Schule erstellen'}</h2>
            {formError && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{formError}</div>}
            <form onSubmit={saveSchool}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name *</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} type="text" required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Kurzname *</label>
                <input value={form.short_name} onChange={(e) => setForm({ ...form, short_name: e.target.value })} type="text" required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">E-Mail *</label>
                <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Adresse</label>
                <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Land</label>
                  <select
                    value={form.country}
                    onChange={(e) => handleCountryChange(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                    disabled={loadingCountries}
                  >
                    {countries.map(c => (
                      <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Region/Kanton</label>
                  <select
                    value={form.region_id}
                    onChange={(e) => setForm({ ...form, region_id: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                  >
                    <option value="">-- Keine --</option>
                    {regions.map(r => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Stadt</label>
                  <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">PLZ</label>
                  <input value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Telefon</label>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Website</label>
                <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Zeitzone</label>
                <input
                  value={form.timezone}
                  onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                  type="text"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                  placeholder="Europe/Zurich"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Währung (unveränderlich)</label>
                <select
                  value={form.billing_currency}
                  onChange={(e) => setForm({ ...form, billing_currency: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                  required
                  disabled={!!editing}
                >
                  <option value="CHF">CHF</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Sprache</label>
                <select
                  value={form.language}
                  onChange={(e) => setForm({ ...form, language: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                >
                  {SCHOOL_LANGUAGES.map(lang => (
                    <option key={lang.code} value={lang.code}>{lang.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Partner</label>
                <select
                  value={form.partner_company_id}
                  onChange={(e) => setForm({ ...form, partner_company_id: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                >
                  <option value="">Direkt</option>
                  {partnerChoices(form.partner_company_id).map((p) => (
                    <option key={p.id} value={p.id}>{p.is_active ? p.name : `${p.name} (inaktiv)`}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button type="button" onClick={closeForm} className="px-4 py-2 border border-gray-300 rounded-md">Abbrechen</button>
              <button type="submit" disabled={submitting} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">{submitting ? (editing ? 'Speichern...' : 'Erstellen...') : (editing ? 'Speichern' : 'Erstellen')}</button>
            </div>
            </form>
          </div>
        </div>
      )}

      {assignSchool && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-full max-w-md space-y-3">
            <h2 className="text-xl font-bold">Zuordnen: {assignSchool.name}</h2>
            {error && <div className="p-3 bg-red-100 text-red-700 rounded">{error}</div>}
            <select className="border w-full p-2" value={assignCompanyId} onChange={(e) => setAssignCompanyId(e.target.value)}>
              <option value="">Direkt</option>
              {partnerChoices(assignCompanyId).map((p) => (
                <option key={p.id} value={p.id}>{p.is_active ? p.name : `${p.name} (inaktiv)`}</option>
              ))}
            </select>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setAssignSchool(null)}>Abbrechen</button>
              <button type="button" className="bg-blue-600 text-white px-3 py-1 rounded" onClick={async () => {
                try {
                  await ultraadminPartnersApi.assignSchool(assignSchool.id, assignCompanyId || null)
                  setAssignSchool(null)
                  await fetchSchools()
                } catch (e: unknown) {
                  setError(extractErrorMessage(e, { loadError: 'Zuordnen fehlgeschlagen' }))
                }
              }}>Speichern</button>
            </div>
          </div>
        </div>
      )}

      {showDomain && selected && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Domain hinzufügen: {selected.name}</h2>
            {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{error}</div>}
            <form onSubmit={addDomain}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Domain *</label>
                <input value={domainForm.domain} onChange={(e) => setDomainForm({ ...domainForm, domain: e.target.value })} type="text" placeholder="school.ch" required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
              </div>
              <div className="flex items-center">
                <input type="checkbox" checked={domainForm.is_primary} onChange={(e) => setDomainForm({ ...domainForm, is_primary: e.target.checked })} className="h-4 w-4 text-blue-600" />
                <label className="ml-2 text-sm text-gray-700">Als primäre Domain setzen</label>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button type="button" onClick={() => setShowDomain(false)} className="px-4 py-2 border border-gray-300 rounded-md">Abbrechen</button>
              <button type="submit" disabled={submitting} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">{submitting ? 'Hinzufügen...' : 'Hinzufügen'}</button>
            </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
